/* 営業日カレンダー */
import React from 'react';
import RowTemplate from '../atoms/RowTemplate';
import moment from 'moment';
import Messages from '../molecules/Messages';
import Paragraph from '../molecules/Paragraph';
import { business_day_calendar as config } from '../Const';
import ja from 'date-fns/locale/ja';
import DatePicker from 'react-datepicker';

interface Props {
  id: string;
  label: string;
  need?: boolean;
  value: Date | null;
  message?: string;
  name?: string;
  placeholder?: string;
  help_msg?: string;
  disabled: boolean;
  handleChange?: (date: Date) => void;
  handleBlur?: VoidFunction;
  handleCalendarClose?: VoidFunction;
}

const BusinessDayCalendar: React.FC<Props> = ({
  id,
  label,
  need,
  value,
  message,
  name,
  placeholder,
  help_msg,
  disabled,
  handleChange = () => {
    return;
  },
  handleBlur,
  handleCalendarClose,
}) => {
  // 開始日から終了日までの年のコレクションを作成する
  const years: number[] = [];
  const dateFrom = moment(new Date()).add(config.min_date, 'd');
  const dateTo = moment(new Date()).add(config.max_year, 'y').subtract('1', 'd');

  for (let i = Number(dateFrom.format('YYYY')); i <= Number(dateTo.format('YYYY')); i++) {
    years.push(i);
  }

  // 月のコレクション
  const months = [
    '1月',
    '2月',
    '3月',
    '4月',
    '5月',
    '6月',
    '7月',
    '8月',
    '9月',
    '10月',
    '11月',
    '12月',
  ];

  /**
   * 年月日の日本語文字列を生成する
   * @return string 日本語の年月日
   */
  const returnStringDateJp = () => {
    if (value !== null && typeof value !== 'undefined') {
      const year = value.getFullYear();
      const month = value.getMonth() + 1;
      const date = value.getDate();
      return `${year}年${month}月${date}日`;
    }
    return '';
  };

  /**
   * 日付加算(日)
   * @param add 追加する日数
   * @return Date 日付オブジェクト
   */
  const addDays = (add: number) => {
    return moment(new Date()).add(add, 'd').toDate();
  };

  /**
   * 日付加算(年)
   * @param add 追加する年数
   * @return Date 日付オブジェクト
   */
  const addYears = (add: number) => {
    return moment(new Date()).add(add, 'y').add(-1, 'd').toDate();
  };

  /**
   * 非活性状態の時に出力するJSX.ELement
   * @returns JSX.Element
   */
  const disabledJsxElement = () => {
    return <Paragraph label={returnStringDateJp()} />;
  };

  const isWeekday = (date: Date) => {
    const day = date.getDay();
    return day !== 0 && day !== 6;
  };

  /**
   * 活性状態の時に出力するJSX.ELement
   * @returns JSX.Element
   */
  const enabledJsxElement = () => {
    return (
      <>
        <DatePicker
          id={id}
          className="form-control"
          disabled={disabled}
          locale={ja}
          onChange={handleChange}
          selected={value ?? null}
          placeholderText={placeholder}
          name={name}
          dateFormat="yyyy年MM月dd日"
          showPopperArrow={false}
          onBlur={handleBlur}
          filterDate={isWeekday}
          minDate={addDays(config.min_date)}
          maxDate={addYears(config.max_year)}
          onCalendarClose={handleCalendarClose}
          renderCustomHeader={({
            date,
            changeYear,
            changeMonth,
            decreaseMonth,
            increaseMonth,
            prevMonthButtonDisabled,
            nextMonthButtonDisabled,
          }) => (
            <div>
              <button
                onClick={decreaseMonth}
                disabled={prevMonthButtonDisabled}
                className="react-datepicker__navigation react-datepicker__navigation--previous"
              >
                {'<'}
              </button>
              <select
                value={date.getFullYear()}
                onChange={({ target: { value } }) => changeYear(Number(value))}
              >
                {years.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <span>年</span>
              <select
                value={months[date.getMonth()]}
                onChange={({ target: { value } }) => changeMonth(months.indexOf(value))}
              >
                {months.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <button
                onClick={increaseMonth}
                disabled={nextMonthButtonDisabled}
                className="react-datepicker__navigation react-datepicker__navigation--next"
              >
                {'>'}
              </button>
            </div>
          )}
        />
        <Messages help_msg={help_msg} valid_msg={message} />
      </>
    );
  };

  return (
    <RowTemplate
      label={label}
      need={need}
      disabled={disabled}
      content={disabled ? disabledJsxElement() : enabledJsxElement()}
    />
  );
};

export default BusinessDayCalendar;
